/**
 * SOLID Review
 * S — Strong. One concern: the client's ONE serial section — load, decide, save, keep — shared by every
 *     context that writes the client's state.
 * O — Good. A new context takes the same steps; nothing here changes.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two steps: one for a table decision, one for a session decision.
 * D — Strong. Two port interfaces, injected.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { IPropsClientChanges, IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { IPortClientState, IPortClientTabStorage } from '../ports/contracts';

interface IEntityFailure {
  readonly success: false;
  readonly error: { readonly code: string; readonly message: string };
}
export type TResult<T> = { readonly success: true; readonly value: T } | IEntityFailure;
/**
 * What every use case and every delivery answers: done, or why not. `value` is OPTIONAL because a void answer
 * that crossed a thread has none — `JSON.stringify` drops `undefined` (the contracts' `ResultVoid`, FE8).
 */
export type TOutcome = { readonly success: true; readonly value?: void } | IEntityFailure;
/** What a decision returns through its port. */
export type TDecision<D> = TResult<{ readonly changes: IPropsClientChanges; readonly dto: D }>;

export const DONE: TOutcome = { success: true, value: undefined };

export function outcomeOf(result: TResult<unknown> | TOutcome): TOutcome {
  return result.success ? DONE : result;
}

export interface IConfigDepClientSteps {
  readonly state: IPortClientState;
  readonly tabStorage: IPortClientTabStorage;
}

/**
 * THE SERIAL SECTION (frontend strict-ports plan, FE1; shared since FE2, 2026-10-03). Ports are
 * asynchronous, so two steps could interleave — load, load, decide, decide, save, save — and the second
 * save would erase the first's changes, from a state read before them. Every step therefore runs on one
 * queue, and nothing inside it awaits the network. A server call sits BETWEEN two steps, never inside
 * one, so a slow server cannot hold the client.
 *
 * FE1 kept this queue inside TableContext. The session writes the same stores — a seat taken by a code
 * is a table change too — so a second queue would let the two contexts erase each other. There is one,
 * here, handed to both.
 *
 * A step that saves changes also keeps what the decision says to keep (`persist`) through the tab's
 * storage, in the same step: a seat taken is a game remembered, with no effect watching for it.
 */
export class ClientSteps {
  private tail: Promise<void> = Promise.resolve();

  constructor(private readonly deps: IConfigDepClientSteps) {}

  private serial<T>(work: () => Promise<T>): Promise<T> {
    const run = this.tail.then(work);
    this.tail = run.then(() => undefined, () => undefined);
    return run;
  }

  /** A table decision's step: it reads the table half of the snapshot. */
  table<D>(ctx: IContextRequest, decide: (input: IPropsClientTableInput) => Promise<TDecision<D>>): Promise<TResult<D>> {
    return this.run(ctx, (loaded) => {
      const input: IPropsClientTableInput = { table: loaded.table };
      return decide(input);
    });
  }

  /** A session decision's step: it reads the table and the session. */
  session<D>(ctx: IContextRequest, decide: (input: IPropsClientInput) => Promise<TDecision<D>>): Promise<TResult<D>> {
    return this.run(ctx, decide);
  }

  /** Load, decide, save what changed, keep what the decision keeps — one indivisible step. */
  private run<D>(ctx: IContextRequest, decide: (input: IPropsClientInput) => Promise<TDecision<D>>): Promise<TResult<D>> {
    return this.serial(async (): Promise<TResult<D>> => {
      const loaded = await this.deps.state.snapshot(ctx);
      if (!loaded.success) return loaded;
      const decided = await decide(loaded.value);
      if (!decided.success) return decided;
      const { changes, dto } = decided.value;
      if (Object.keys(changes).length > 0) {
        const saved = await this.deps.state.apply(ctx, changes);
        if (!saved.success) return saved;
      }
      if (changes.persist !== undefined) {
        const kept = await this.deps.tabStorage.persist(ctx, changes.persist);
        if (!kept.success) return kept;
      }
      return { success: true, value: dto };
    });
  }
}
